/**
 * VerificationScreenTemplate Component
 * Base template for all verification flow screens
 */

import type { ReactNode } from 'react';
import { BaseScreen } from './BaseScreen';
import { designTokens } from '../../constants/designTokens';
import { useDisplayModeContext } from '../../hooks/useDisplayModeContext';
import {
  AppContainer,
  Logo,
  ScreenTitle,
  ScreenDescription,
  IconDisplay,
  PrimaryButton,
  VerticalStack,
  OAuthButton,
  Separator,
} from './index';

interface VerificationScreenTemplateProps {
  // Screen metadata
  className?: string;
  showProgress?: boolean;
  showBackButton?: boolean;

  // Content props
  title: string;
  description?: string;
  icon?: {
    src: string;
    darkSrc?: string;
    alt: string;
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
    width?: number;
    height?: number;
    animated?: boolean;
  };

  // Action props
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };

  secondaryAction?: {
    label: string;
    onClick: () => void;
    icon?: string;
    darkIcon?: string;
  };

  separatorText?: string;

  // Desktop optimization
  desktopOptimized?: boolean;

  // Logo visibility (default: true)
  showLogo?: boolean;

  // Additional content
  children?: ReactNode;
  footer?: ReactNode;
  
  // Content to display after actions
  afterActions?: ReactNode;
}

export function VerificationScreenTemplate({
  className = '',
  showProgress = true,
  showBackButton = true,
  title,
  description,
  icon,
  primaryAction,
  secondaryAction,
  separatorText,
  desktopOptimized = false,
  showLogo = true,
  children,
  footer,
  afterActions,
}: VerificationScreenTemplateProps) {
  const { isStandalone } = useDisplayModeContext();

  return (
    <BaseScreen
      className={`verification-screen-template ${className}`}
      showProgress={showProgress}
      showBackButton={showBackButton}
    >
      <AppContainer maxWidth={isStandalone ? 'desktopWide' : 'desktop'}>
        <VerticalStack
          spacing="sm"
          align="center"
          className="template-main-stack"
          style={{ minHeight: '100%' }}
        >
          {/* Logo */}
          {showLogo && <Logo desktopEnhanced={desktopOptimized} />}

          {/* Content Section */}
          <VerticalStack spacing="xs" align="center">
            {/* Icon */}
            {icon && (
              <IconDisplay
                src={icon.src}
                darkSrc={icon.darkSrc}
                alt={icon.alt}
                size={icon.size}
                width={icon.width}
                height={icon.height}
                animated={icon.animated}
                desktopScaled={desktopOptimized}
              />
            )}

            {/* Title */}
            <ScreenTitle desktopEnhanced={desktopOptimized}>
              {title}
            </ScreenTitle>

            {/* Description */}
            {description && (
              <ScreenDescription desktopEnhanced={desktopOptimized}>
                {description}
              </ScreenDescription>
            )}

            {/* Custom content */}
            {children}
          </VerticalStack>

          {/* Actions */}
          {(primaryAction || secondaryAction) && (
            <VerticalStack spacing="md" align="stretch" className="template-actions">
              {primaryAction && (
                <PrimaryButton
                  onClick={primaryAction.onClick}
                  disabled={primaryAction.disabled}
                  loading={primaryAction.loading}
                  fullWidth={!desktopOptimized}
                >
                  {primaryAction.label}
                </PrimaryButton>
              )}

              {separatorText && secondaryAction && (
                <Separator text={separatorText} />
              )}

              {secondaryAction && (
                secondaryAction.icon ? (
                  <OAuthButton
                    label={secondaryAction.label}
                    icon={secondaryAction.icon}
                    darkIcon={secondaryAction.darkIcon}
                    onClick={secondaryAction.onClick}
                    fullWidth={!desktopOptimized}
                  />
                ) : (
                  <PrimaryButton
                    onClick={secondaryAction.onClick}
                    variant="secondary"
                    fullWidth={!desktopOptimized}
                  >
                    {secondaryAction.label}
                  </PrimaryButton>
                )
              )}
            </VerticalStack>
          )}

          {/* Content after actions */}
          {afterActions}

          {/* Footer */}
          {footer && <div className="template-footer">{footer}</div>}
        </VerticalStack>
      </AppContainer>

      <style>{`
        .verification-screen-template .screen-content {
          /* Clean, flat design - no container effects */
          border: none;
          box-shadow: none;
          background: transparent;
        }
        
        /* Remove default padding-top in standalone mode - already handled by BaseScreen */
        .screen-container[data-display-mode="standalone"] .verification-screen-template .screen-content {
          padding-top: 0;
        }
        
        
        .template-footer {
          margin-top: auto;
          padding-top: ${designTokens.spacing.xl};
          color: ${designTokens.colors.text.secondary};
          font-size: ${designTokens.typography.fontSize.sm};
          text-align: center;
          
          /* Mobile-First: Better footer spacing */
          width: 100%;
        }
        
        /* Mobile optimizations (up to 767px) */
        ${designTokens.media.mobile} {
          .verification-screen-template .screen-content {
            padding-top: ${designTokens.spacing.md};
            padding-bottom: calc(${designTokens.spacing.md} + env(safe-area-inset-bottom));
            /* Ensure buttons stay above virtual keyboard and Safari UI */
            min-height: 100vh;
            min-height: 100dvh;
          }
          
          .template-main-stack {
            gap: ${designTokens.spacing.lg}; /* Reduced spacing for mobile */
            min-height: calc(100vh - ${designTokens.spacing.md} * 2);
            min-height: calc(100dvh - ${designTokens.spacing.md} * 2);
            justify-content: space-between;
          }
          
          .template-footer {
            padding-top: ${designTokens.spacing.lg};
            font-size: ${designTokens.typography.fontSize.xs};
          }
          
          .template-actions {
            margin-top: auto;
            flex-shrink: 0;
            background: var(--verify-screen-bg);
            padding-top: ${designTokens.spacing.sm};
            padding-bottom: env(safe-area-inset-bottom);
          }
        }
        
        /* Mobile landscape optimizations */
        @media (max-height: 500px) and (orientation: landscape) {
          .verification-screen-template .screen-content {
            min-height: 100vh;
            min-height: 100dvh;
            padding-top: ${designTokens.spacing.sm};
            padding-bottom: calc(${designTokens.spacing.sm} + env(safe-area-inset-bottom));
          }
          
          .template-footer {
            padding-top: ${designTokens.spacing.md};
          }
          
          .template-actions {
            position: static;
            margin-top: ${designTokens.spacing.sm};
          }
        }
        
        /* Tablet (768px-1023px) - Enhanced spacing */
        ${designTokens.media.tablet} {
          .verification-screen-template .screen-content {
            padding-bottom: ${designTokens.spacing.xl};
          }
          
          .template-footer {
            padding-top: ${designTokens.spacing['2xl']};
            font-size: ${designTokens.typography.fontSize.base};
          }
        }
        
        /* Desktop final spacing (1024px+, no further scaling) */
        ${designTokens.media.desktop} {
          .template-main-stack {
            gap: ${designTokens.spacing.md}; /* Standard spacing - not excessive */
          }
          
          .verification-screen-template .screen-content {
            padding-bottom: ${designTokens.spacing['2xl']};
          }
          
          .template-footer {
            padding-top: ${designTokens.spacing['2xl']};
            font-size: ${designTokens.typography.fontSize.base};
          }
        }
      `}</style>
    </BaseScreen>
  );
}
